import React from "react";
import { motion, useInView } from "framer-motion";

/**
 * MaskedReveal: line-by-line masked reveal.
 * Usage: <MaskedReveal lines={["Line one", "Line two"]} className="serif text-6xl" />
 */
export function MaskedReveal({ lines = [], className = "", delay = 0, stagger = 0.09, as = "h2", once = true, testid, nowrap = false }) {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once, margin: "-10% 0px -10% 0px" });
  const Tag = motion[as] || motion.h2;

  return (
    <Tag ref={ref} className={className} data-testid={testid}>
      {lines.map((line, i) => (
        <span key={i} style={{ display: "block", overflow: "hidden" }}>
          <motion.span
            style={{ display: "inline-block", willChange: "transform", whiteSpace: nowrap ? "nowrap" : "normal" }}
            initial={{ y: "110%" }}
            animate={inView ? { y: "0%" } : { y: "110%" }}
            transition={{ delay: delay + i * stagger, duration: 0.95, ease: [0.7, 0.05, 0.2, 1] }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
}

/**
 * FadeUp: subtle fade + rise on scroll.
 */
export function FadeUp({ children, delay = 0, y = 24, className = "", once = true, testid }) {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once, margin: "-10% 0px -10% 0px" });
  return (
    <motion.div
      ref={ref}
      data-testid={testid}
      className={className}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ delay, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

/**
 * ClipImage: image entering with clip-path reveal.
 */
export function ClipImage({ src, alt, className = "", imgClassName = "", direction = "bottom", testid }) {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px -10% 0px" });
  const initial =
    direction === "bottom" ? { clipPath: "inset(0 0 100% 0)" } :
    direction === "top" ? { clipPath: "inset(100% 0 0 0)" } :
    direction === "left" ? { clipPath: "inset(0 100% 0 0)" } :
    { clipPath: "inset(0 0 0 100%)" };
  return (
    <div ref={ref} className={className} data-testid={testid}>
      <motion.div
        initial={initial}
        animate={inView ? { clipPath: "inset(0 0 0 0)" } : initial}
        transition={{ duration: 1.15, ease: [0.7, 0.05, 0.2, 1] }}
        className="w-full h-full"
      >
        <img src={src} alt={alt} className={imgClassName} loading="lazy" />
      </motion.div>
    </div>
  );
}
